import { useEffect, useMemo, useState, useCallback, memo } from "react";
import { Division, DocumentViewer, Loader } from "@ir/ui";
import { useSetAtom } from "jotai";
import { setCloseDocumentViewerAtom, setViewerDocumentAtom } from "@/store/deduction/drawer";
import { DeductionDocument } from "@/core/entities/deduction";
import { GetDocumentSignedURLUseCase } from "@/core/usecases/document";
import { DocumentSignedURLRepository } from "@/infrastructure/repositories/document";
import { GraphQlAdapter } from "@/infrastructure/adapters/graphQl";
import { useWindowSize } from "@/hooks/measurements/useWindowSize";
import { createLogger } from "@/infrastructure/services/logger";

interface DeductionDocumentViewerProps {
  documents: DeductionDocument[];
  invoiceNumber: string;
  className?: string;
  style?: React.CSSProperties;
}

// GraphQL client
const client = GraphQlAdapter.getInstance().getClient();
// Deduction document signed URL repository
const documentSignedURLRepository = new DocumentSignedURLRepository(client);
const logger = createLogger("DeductionDocumentViewer");

const DeductionDocumentViewer = memo(({ documents, invoiceNumber, className, style }: DeductionDocumentViewerProps) => {
  const setViewerDocuments = useSetAtom(setViewerDocumentAtom);
  const setCloseDocumentViewer = useSetAtom(setCloseDocumentViewerAtom);
  const { width, height } = useWindowSize();
  const [isLoading, setIsLoading] = useState(false);

  const memoizedDocuments = useMemo(() => documents, [documents]);

  const fetchDocumentSignedURL = useCallback(async (file_url: string, file_type: string, downloadable_file_name: string) => {
    try {
      const response = await new GetDocumentSignedURLUseCase(documentSignedURLRepository).execute(
        file_url,
        file_type,
        downloadable_file_name
      );
      return response;
    } catch (error) {
      logger.error("Error fetching document signed URL", error);
      return null;
    }
  }, []);

  const setDocumentsWithSignedURL = useCallback(
    async (documents: DeductionDocument[]): Promise<void> => {
      if (!documents?.filter(document => !document.signedUrl || document.isExpired).length) return;

      const documentsWithSignedURLs: DeductionDocument[] = (
        await Promise.all(
          documents.map(async document => {
            if (document.signedUrl && !document.isExpired) {
              return document;
            }

            let downloadableFileName = document.fileDisplayName;
            if (!document.deletable) {
              const fileExtension = document.fileDisplayName.split(".").pop();
              downloadableFileName = invoiceNumber && document.documentType?.toUpperCase() === "BACKUP" ? `${invoiceNumber}.${fileExtension}` : document.fileDisplayName;
            }

            const response = await fetchDocumentSignedURL(document.filePath, document.fileMimeType, downloadableFileName);

            if (response?.signedUrl) {
              document.signedUrl = response.signedUrl;
              return document;
            }

            return null;
          })
        )
      ).filter((url): url is DeductionDocument => url !== null);

      documents = documentsWithSignedURLs;
      setViewerDocuments(documents);
    },
    [setViewerDocuments, fetchDocumentSignedURL, invoiceNumber]
  );

  const memoizedInitializer = useMemo(
    () => ({
      initialize: async () => {
        try {
          setIsLoading(true);
          await setDocumentsWithSignedURL(memoizedDocuments);
        } catch (error) {
          setIsLoading(false);
          logger.error("Error setting documents with signed URL", error);
          return;
        }
        setIsLoading(false);
      },
    }),
    [memoizedDocuments, setDocumentsWithSignedURL]
  );

  useEffect(() => {
    if (width < 1200) {
      setCloseDocumentViewer();
    }
  }, [width, setCloseDocumentViewer]);

  useEffect(() => {
    memoizedInitializer.initialize();
  }, [memoizedInitializer]);

  const handleCloseDocumentViewer = useCallback(() => {
    setCloseDocumentViewer();
  }, [setCloseDocumentViewer]);

  return (
    <Division className="document-viewer">
      {isLoading ? (
        <Loader loading={isLoading} className={isLoading ? undefined : "d-none"} />
      ) : (
        <DocumentViewer
          onClose={handleCloseDocumentViewer}
          documents={memoizedDocuments?.map(document => ({
            id: document.id,
            mimeType: document.fileMimeType,
            url: document.signedUrl,
            name: document.fileDisplayName,
          }))}
          height={height}
          className={`${className}`}
          style={{
            ...style,
          }}
        />
      )}
    </Division>
  );
});

DeductionDocumentViewer.displayName = "DeductionDocumentViewer";
export default DeductionDocumentViewer;
